import Head from "next/head";
import TestimonialCard from "components/CLM/CaseStudyCard";
import CLMHero2 from "components/CLM/CLMHero2";
import CTASection from "components/CLM/CTASection";
import FAQ2 from "components/CLM/FAQ2";
import ProactiveCard from "components/CLM/ProactiveCard";
import { CLMButtonPrimary } from "components/CLM/v3/Buttons";
import Image from "next/image";
import { BASE_ASSETS_URL } from "@constants/constants";

const CLMHeroData = {
  // title: "Reporting",
  heading: "Empower legal teams for managing online terms",
  description:
    "Easily organize, edit, and publish your website’s terms and policies—all from one place.",
  image: `${BASE_ASSETS_URL}/v2/clm/clm/legalhub-header-1.png`,
  btnText: "Review a contract for free",
  btnUrl: "/clm/request-demo",
};

const threeCards = [
  {
    img: `${BASE_ASSETS_URL}/v2/clm/clm/legal-hub-effortless.webp`,
    alt: "filter-by",
    desc: "Draft, edit, and format online terms in a familiar interface—just like Word or Google Docs.",
    title: "Effortless editing experience",
  },
  {
    img: `${BASE_ASSETS_URL}/v2/clm/clm/legal-hub-identify-2.png`,
    alt: "expiry-is",
    desc: "Deliver tailored terms by grouping them by product, region, or customer type.",
    title: "Identifying the right terms",
  },
  {
    img: `${BASE_ASSETS_URL}/v2/clm/clm/legal-hub-one-click-copy.png`,
    alt: "custom-view",
    desc: "Capture legally binding agreement instantly, with version tracking and audit trails built-in.",
    title: "One-click legal consent",
  },
];

const legalHubFaqData = [
  {
    question: "What is BoloSign Legal Hub?",
    answer:
      "BoloSign Legal Hub is your centralized workspace for publishing and managing standard agreements online. It gives legal and operations teams complete oversight, making it easy to organize, update, and distribute documents across departments.",
  },
  {
    question: "How quickly can I deploy the Legal Hub?",
    answer:
      "You can get your Legal Hub live within minutes. It's easy to configure with your brand’s logo, colors, and domain—no need for extensive dev support or long implementation timelines.",
  },
  {
    question: "How does the Legal Hub support agreement management?",
    answer:
      "With BoloSign Legal Hub, you can maintain all agreement versions in one place, edit terms as needed, and ensure every stakeholder always sees the most current version—without chasing down other teams.",
  },
  {
    question: "Can I connect BoloSign Legal Hub with other tools or web pages?",
    answer:
      "Absolutely. You can embed your agreements across multiple touchpoints—whether it’s a sign-up page, app flow, or footer—so legal documents are always accessible where your users need them.",
  },
  {
    question: "What are some standout capabilities of BoloSign Legal Hub?",
    answer:
      "From a Microsoft Word-like editor to smart filtering by region or product line, BoloSign lets you present the right contracts to the right users. It also allows single-click acceptance and version tracking—streamlining everything from rollout to audit.",
  },
  {
    question: "Who is BoloSign Legal Hub built for?",
    answer:
      "It’s designed for legal and operations teams at scaling companies that want more control over contract workflows—without slowing down product, marketing, or customer teams.",
  },
  {
    question: "How does BoloSign ensure contract compliance and integrity?",
    answer:
      "BoloSign tracks every change with built-in version control and maintains an audit history of all interactions. That means your contracts stay current, compliant, and provably secure—every step of the way.",
  },
];

// function ProactiveCard({
//   title,
//   desc,
//   videoSrc,
//   imgSrc,
//   isImageRight = true,
//   bgColor = "bg-white",
// }) {
//   return (
//     <div
//       className={`flex flex-col md:flex-row gap-8 md:gap-12 md:my-12 ${bgColor}`}
//     >
//       <div
//         className={`flex-1 text-start flex flex-col justify-center items-center ${
//           bgColor == "bg-black" ? "text-white" : "text-gray-800"
//         } ${isImageRight ? "md:order-1" : "md:order-2"}`}
//       >
//         <h2 className="text-4xl md:text-5xl font-semibold mb-4 md:mb-8">
//           {title}
//         </h2>
//         <p
//           className={`text-lg font-medium ${
//             bgColor == "bg-black" ? "text-white" : "text-gray-600"
//           } md:text-2xl md:mb-9`}
//         >
//           {desc}
//         </p>
//       </div>
//       <div className={` ${isImageRight ? "md:order-2" : "md:order-1"} flex-1`}>
//         {videoSrc && (
//           <video
//             src={videoSrc}
//             alt="ai-agent"
//             width={500}
//             height={500}
//             className={`rounded-xl w-full`}
//             autoPlay
//             loop
//             muted
//           />
//         )}
//         {imgSrc && (
//           <Image
//             src={imgSrc}
//             alt="ai-agent"
//             width={500}
//             height={500}
//             className={`rounded-xl  w-full`}
//           />
//         )}
//       </div>
//     </div>
//   );
// }

const LegalHub = () => {
  return (
    <>
      <Head>
        <title>Legal Hub - BoloSign CLM</title>
        <meta
          name="description"
          content="Centralize, manage, and publish your online terms with BoloSign Legal Hub—designed for legal teams to maintain compliance and control."
        />
        <meta
          name="keywords"
          content="BoloSign Legal Hub, online terms management, contract lifecycle management, CLM legal hub, centralized policy management, legal document publishing, contract compliance, legal operations tools"
        />
        <link rel="canonical" href="https://www.bolosign.com/clm/legal-hub/" />
      </Head>
      <CLMHero2 CLMHeroData={CLMHeroData} />
      <div className="text-start md:text-center bg-cream py-20">
        <h2 className="text-4xl md:text-5xl font-semibold mb-6 px-6 md:px-0">
          Full ownership of your digital policies
        </h2>
        <p className="text-xl lg:w-[70%] xl:w-[60%] mx-auto px-6 md:px-0">
          With BoloSign, legal teams can create, update, and publish terms—all
          in one place, no tech help needed.
        </p>
      </div>

      <section className="bg-dark">
        <div className="max-w-screen-xl md:px-8 px-6 mx-auto py-16 md:py-32 text-gray-900 text-center">
          <ProactiveCard
            imgSrc={`${BASE_ASSETS_URL}/v2/clm/clm/legal-hub-legal.png`}
            title="Update terms instantly—no follow-ups needed"
            desc="Make changes to your terms or agreements whenever you want. BoloSign saves each version automatically—no need to wait on marketing or dev teams."
            isImageRight={false}
            bgColor="bg-dark"
          />
        </div>
      </section>

      <section className="">
        <div className="max-w-screen-xl md:px-8 px-6 mx-auto pt-16 md:pt-32 text-gray-900 text-center">
          <ProactiveCard
            imgSrc={`${BASE_ASSETS_URL}/v2/clm/clm/legal-hub-royal.png`}
            title="One central hub for every policy and update"
            desc="Easily manage, publish, and access all your legal terms from a single dashboard—so your users always see the latest version."
            isImageRight={true}
            bgColor=""
          />
        </div>
      </section>

      <section className="bg-transparent">
        <div className="max-w-screen-xl md:px-8 px-6 mx-auto pt-16 md:pt-32 text-gray-900 text-center">
          <ProactiveCard
            imgSrc={`${BASE_ASSETS_URL}/v2/clm/clm/legal-hub-launch.png`}
            title="Launch your branded legal hub in minutes"
            desc="Set up your own policy center with BoloSign, fully customized to match your brand—no dev work or long setup required."
            isImageRight={false}
            bgColor="bg-transparent"
          />
        </div>
      </section>

      <section className="">
        <div className="max-w-screen-xl md:px-8 px-6 mx-auto py-16 md:py-32 text-gray-900 text-center">
          <ProactiveCard
            imgSrc={`${BASE_ASSETS_URL}/v2/clm/clm/legal-hub-clickwrap.png`}
            title="Embed online terms wherever they matter most"
            desc="Add your online terms to contracts, sign-up flows, or footers—making them accessible, compliant, and crystal clear across all touchpoints."
            isImageRight={true}
            bgColor=""
          />
        </div>
      </section>

      <div className="text-center bg-dark text-white md:py-24 py-16">
        <h2 className="text-4xl md:text-5xl mb-6">
          Why teams choose BoloSign legal hub
        </h2>
        <p className="text-xl md:text-2xl lg:w-[70%] xl:w-[60%] mx-auto max-w-screen-lg mx-auto px-6 md:px-0">
          BoloSign lets you draft, edit, and review contracts using Microsoft
          Word’s interface—plus advanced tools built for legal teams.
        </p>
      </div>

      <section className="text-black py-24 px-6 md:px-8">
        <div className="max-w-screen-xl xl:px-8 mx-auto flex flex-col md:flex-row gap-12 justify-between items-end">
          {threeCards.map((item, index) => (
            <div
              key={index}
              className=" flex flex-col items-center justify-between gap-3"
            >
              <div className="text-start">
                <p className="text-2xl font-semibold mb-1">{item.title}</p>
                <p className="text-base mb-3">{item.desc}</p>
              </div>
              <Image
                src={item.img}
                alt={item.alt}
                width={300}
                height={300}
                className="w-full h-full "
              />
            </div>
          ))}
        </div>
      </section>

      <div className="text-center bg-dark text-white py-24">
        <h2 className="text-4xl md:text-5xl mb-8 max-w-screen-xl mx-auto md:px-24">
          Discover how BoloSign helps legal teams to manage online terms
          effortlessly
        </h2>
        <CLMButtonPrimary
          buttonText="Request a quick demo"
          buttonUrl="/clm/request-demo"
          className="w-fit mx-auto"
        />
      </div>

      <TestimonialCard
        logo={`${BASE_ASSETS_URL}/v2/clm/clm/legal-hub-logo.webp`}
        desc="“We had contracts ready, but no centralized place to manage or share them”"
        subHead="Jane Roberts, Legal Operations Lead, Rethink Electric"
        bgColor="bg-transparent"
        image={`${BASE_ASSETS_URL}/v2/clm/clm/legal-hub-laptop-in-hand-1.png`}
      />

      <section className="bg-[#f4f2f0] text-black py-16 md:px-10 px-4 xl:px-20 text-center md:pt-24 pt-12 ">
        <FAQ2 faqData={legalHubFaqData} heading={"FAQs"} />
      </section>
    </>
  );
};

export default LegalHub;
